import { createContext, useContext, useEffect, useReducer } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider';

import useFetchNotifications from '../features/feeds/useFetchNotitifications';

const initialState = {
  unread: 0,
  unseen: 0,
};

const FeedsContext = createContext(initialState);

const feedsReducer = (state, action) => {
  const { type, ...payload } = action;

  switch (type) {
    case '@feeds/update-meta':
      return { ...state, ...payload };
    default:
      throw new Error('Feeds reducer message error');
  }
};

export const FeedsProvider = ({ children }) => {
  const { client, mutateApi } = useApi();

  const [state, dispatch] = useReducer(feedsReducer, initialState);

  // feeds cache key already exists
  const {
    query: { data },
    enableFeeds,
    disableFeeds,
  } = useFetchNotifications({
    refetchInterval: 120 * 1000,
  });

  const clearCache = () => {
    client.invalidateQueries(['feeds']);
    client.invalidateQueries(['feeds/notifications']);
  };

  const mutateSee = useMutation(
    () =>
      mutateApi(['feeds/seen_all'], null, {
        method: 'POST',
      }),
    {
      onSuccess: clearCache,
    },
  );

  const mutateRead = useMutation(
    feed =>
      mutateApi(['feeds', feed.id], feed, {
        method: 'PUT',
      }),
    {
      onSuccess: clearCache,
      onMutate: async feed => {
        const queryKey = ['feeds'];

        await client.cancelQueries({ queryKey });
        const previousFeeds = client.getQueryData(queryKey);

        client.setQueryData(queryKey, old => {
          return {
            ...old,
            pages: old.pages.map(page => {
              return {
                ...page,
                data: page.data.map(data => {
                  if (data.id !== feed.id) {
                    return data;
                  }

                  return {
                    ...data,
                    is_read: true,
                  };
                }),
              };
            }),
          };
        });

        return { previousFeeds };
      },
    },
  );

  const seeFeeds = () => {
    mutateSee.mutate();
  };

  const readFeed = id => {
    mutateRead.mutate({ id, is_read: true });
  };

  useEffect(() => {
    if (!data) return;
    if (!data.meta) return;

    dispatch({ type: '@feeds/update-meta', ...data.meta });
  }, [data]);

  return (
    <FeedsContext.Provider
      value={{ state, seeFeeds, readFeed, dispatch, enableFeeds, disableFeeds }}
    >
      {children}
    </FeedsContext.Provider>
  );
};

// Contexto de descargas
export const useFeeds = () => useContext(FeedsContext);
